import { createTheme } from "@mui/material/styles";
import { red, yellow } from "@mui/material/colors";

const theme = createTheme({
  palette: {
    primary: {
      main: "#4AB9C6", // --theme-color
      light: "#DEF1F3", // --light-theme-cyan
      dark: "#90CFD6", // --light-theme-color
      contrastText: "#FFFFFF", // --theme-white
      // contrastText: "#5F6B6D", // --theme-gray
    },
    secondary: {
      main: "#1B2C2E", // --theme-black
      light: "#1C2C2E", // --theme-black-second
      dark: "#231F20", // --theme-hover
      contrastText: "#A9B3B5", // --theme-placeholder-gray
    },
    error: {
      main: "#EF0909", // --theme-red
      light: "#F7A979", // --theme-orange
      dark: red[700], // Dark Error Red
      contrastText: "#FFFFFF", // --theme-white
    },
    success: {
      main: "#3B9411", // --theme-green
      light: "#CEEBEE", // Light Green
      dark: "#BCE3E7", // -- table-border-color
      contrastText: "#FFFFFF", // --theme-white
    },
    info: {
      main: "#0000EE", // --theme-blue
      light: "#9747FF", // --theme-purple
      dark: "#A6DCE2", // --theme-third-shade
      contrastText: "#5F6B6D", // --theme-gray
    },
    warning: {
      main: "#F7A979", // --theme-orange
      light: "#F8FDFE", // -- Second-White
      dark: yellow[900], // Dark Yellow
      contrastText: "#000000", // --main-black
    },
    background: {
      default: "#FFFFFF", // --theme-white
      paper: "#FFFFFF", // --theme-white
    },
    text: {
      primary: "#1B2C2E", // --theme-black
      secondary: "#1C2C2E", // --theme-black-second
      disabled: "#5F6B6D", // --theme-gray
    },
  },

  typography: {
    h1: {
      fontSize: "24px", // 2rem converted to px (1rem = 16px)
      fontWeight: 700,
      lineHeight: 1.2,
      color: "#1B2C2E",
    },
    h2: {
      fontSize: "20px", // 1.75rem converted to px
      fontWeight: 600,
      lineHeight: 1.3,
      color: "#1B2C2E",
    },
    h3: {
      fontSize: "18px", // Assuming you wanted 20px for this as per your comment
      fontWeight: 600,
      lineHeight: 1.3,
      color: "#1B2C2E",
    },
    h4: {
      fontSize: "16px",
      "@media screen and (max-width: 991.5px)": {
        fontSize: "18px", // Larger size on larger screens
      },
    },
    h5: {
      fontSize: "14px",
      fontWeight: "400",
      "@media screen and (max-width: 991.5px)": {
        fontSize: "16px", // Larger size on larger screens
      },
    },
    body1: {
      fontSize: "15px", // 1rem converted to px
      fontWeight: 400,
      color: "#1B2C2E",
    },
    body2: {
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: 1.5,
      color: "#1B2C2E",
    },
    caption: {
      fontSize: "12px",
      fontWeight: 400,
      color: "#1B2C2E",
      "@media (min-width:1700px) and (max-width:2500px)": {
        fontSize: "14px",
      },
      // "@media (min-width:2501px) and (max-width:6000px)": {
      //   fontSize:"18px",
      // },
    },
    button: {
      fontSize: "14px",
      fontWeight: 400,
      textTransform: "uppercase",
      color: "#1B2C2E",
      "@media (min-width:1700px) and (max-width:2500px)": {
        fontSize: "16px",
      },
      "@media (min-width:1025px) and (max-width:1699px)": {
        fontSize: "13px",
      },
      "@media (min-width:991.5px)": {
        fontSize: "13px",
      },
    },
    subtitle1: {
      fontSize: "16px",
      fontWeight: 600,
      lineHeight: 1.5,
      color: "#1B2C2E",
    },
    subtitle2: {
      fontSize: "13px",
      fontWeight: 400,
      lineHeight: 1.5,
      color: "#1B2C2E",
      "@media (min-width:1700px) and (max-width:2500px)": {
        fontSize: "16px",
      },
      // "@media (min-width:2501px) and (max-width:6000px)": {
      //   fontSize:"20px",
      // },
    },
  },

  components: {
    // Button CSS :: START
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: "15px",
          padding: "5px 20px",
          // fontWeight:'400',
          fontFamily: "Nunito-Bold !important",
          textTransform: "none", // Keep text capitalization as is
          borderRadius: 8, // Rounded corners for buttons
          boxShadow: "none", // Remove box shadow
          "&:hover": {
            backgroundColor: "white", // Hover background color
            border: "1px solid", // Border on hover
          },
        },
        containedPrimary: {
          color: "#FFFFFF", // White text on primary buttons
          border: "1px solid #4AB9C6", // Border on hover
          backgroundColor: "#4AB9C6", // Primary background color
          "&:hover": {
            backgroundColor: "#FFF", // Darker on hover
            border: "1px solid #4AB9C6", // Border on hover
            color: "#4AB9C6", // White text on primary buttons
            boxShadow: "none",
          },
        },
        containedSecondary: {
          color: "#FFFFFF", // White text on secondary buttons
          backgroundColor: "#2B2A2A", // Secondary background color
          "&:hover": {
            backgroundColor: "#1A1A1A", // Darker on hover
            border: "1px solid", // Border on hover
          },
        },
      },
    },
    // Button CSS :: END

    // Appbar CSS :: START
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent", // Primary color for the AppBar
          color: "#FFFFFF", // White text
        },
      },
    },
    // Appbar CSS :: END

    // Drawer CSS :: START
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#f5f5f5", // Example background color
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)", // Example box shadow
        },
      },
    },
    // Drawer CSS :: END

    // Paper CSS :: START
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          backgroundColor: "#FFFFFF", // Paper components (cards, etc.)
          color: "#2B2A2A", // Main text color
        },
      },
    },
    // Paper CSS :: END

    // IconButton CSS :: START
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&.MuiIconButton-colorPrimary": {
            color: "#FFFFFF",
            backgroundColor: "#CB0000",
            "&:hover": {
              backgroundColor: "#AA0000",
            },
          },
          "&.MuiIconButton-colorSecondary": {
            color: "#4AB9C6",
            "&:hover": {
              backgroundColor: "#4AB9C6",
              color: "#FFFFFF",
            },
          },
        },
      },
    },
    // IconButton CSS :: END

    // FormControlLabel CSS :: START
    MuiFormControlLabel: {
      styleOverrides: {
        asterisk: {
          color: red.A700,
        },
        root: {
          backgroundColor: "#F0F0F0", // Change background to light gray for better contrast
        },
      },
    },
    // FormControlLabel CSS :: END

    // FormLabel CSS :: START
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          color: red.A700,
          // fontSize: "25px",
        },
        root: {
          // backgroundColor: '#ffffff', // Change background to light gray
          // padding: '0 8px',
        },
      },
    },
    // FormLabel CSS :: END

    //-----------// Table CSS :: START

    // Table CSS :: START
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: "#F4F4F4", // Light background for tables
        },
      },
    },
    // Table CSS :: END

    // TableCell CSS :: START
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "3px 5px", // Compact padding for table cells
        },
        head: {
          fontWeight: 700, // Bold header cells
          backgroundColor: "#CB0000", // Red header background
          color: "#FFFFFF", // White text for headers
        },
        body: {
          fontWeight: 400, // Normal weight for body cells
          color: "#2B2A2A", // Dark text for body cells
        },
      },
    },
    // TableCell CSS :: END

    // TableRow CSS :: START
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:nth-of-type(odd)": {
            backgroundColor: "#FFFFFF", // White for odd rows
          },
          "&:nth-of-type(even)": {
            backgroundColor: "#F9F9F9", // Slight gray for even rows
          },
        },
      },
    },
    // TableRow CSS :: END

    //-----------// Table CSS :: START

    // TextField CSS :: START
    MuiTextField: {
      styleOverrides: {
        root: {
          "& label": {
            fontSize: "14px !important",
          },
          "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
            "-webkit-appearance": "none",
            margin: 0,
          },
          "& input[type=number]": {
            "-moz-appearance": "textfield",
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              border: "1px solid #4AB9C6", // Set border color (default state)
              borderRadius: "8px",
            },
            "&:hover fieldset": {
              borderColor: "#4AB9C6", // Border color on hover
            },
            "&.Mui-focused fieldset": {
              borderColor: "#4AB9C6", // Border color when focused
              borderWidth: "1px", // Optional: Change border width when focused
            },
            "& .MuiInputBase-input": {
              height: "15px",
            },
            "& .MuiSvgIcon-root": {
              color: "#4AB9C6",
              fontSize: "25px",
              borderRadius: "5px",
            },
            "&.Mui-disabled .MuiInputBase-input, & .MuiInputBase-input[readonly]": {
              cursor: "not-allowed !important", // Add cursor styling for disabled or readonly inputs
              backgroundColor: "#F9F9F9",
            },
          },
        },
      },
    },


    // TextField CSS :: END

    // Input CSS :: START
    MuiInputLabel: {
      styleOverrides: {
        root: {
          borderRadius: "10px !important",
          fontSize: "14px", // Set the label font size here
          color: "#A9B3B5", // Optionally change the default label color
          transform: "translate(14px, 10px) scale(1)",
          // transform: 'translate(14px, 14px) scale(1)',
          // transition: 'transform 0.3s ease-in-out',
          "&.Mui-focused": {
            color: "#4AB9C6 !important", // Label color when focused
          },
          "&.MuiInputLabel-shrink": {
            color: "#4AB9C6 !important", // Label color when input has content
            fontSize: "16px !important", // Font size when input has content
            fontWeight: "600", // Font weight when input has content
            transform: "translate(9px, -9px) scale(0.75)",
            padding: "0 10px !important",
            background: "#fff",
          },
        },
      },
    },
    // Input CSS :: END

    // Tabs CSS :: START
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
    // Tabs CSS :: END

    // Checkbox CSS :: START
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: "#4AB9C6", // Default checkbox color
          width: "34px", // Custom width
          height: "34px", // Custom height
          "&.Mui-checked": {
            color: "#4AB9C6", // Checkbox color when checked
          },
        },
      },
    },
    // Checkbox CSS :: END

    // RadioButton CSS :: START
    MuiRadio: {
      styleOverrides: {
        root: {
          background: "none",
          color: "#4AB9C6", // Default color
          width: "30px", // Custom width
          height: "30px", // Custom height
          "&.Mui-checked": {
            color: "#4AB9C6", // Checked color
          },
        },
      },
    },
    MuiRadioGroup: {
      styleOverrides: {
        root: {
          background: "none !important", // Ensure no background
        },
      },
    },
    // RadioButton CSS :: END
  },
});

// Alignment Box CSS :: START

export default theme;

// export { AlignBox };
